import React, { FC, useEffect } from 'react';

import { CloudProviders } from '@ariksa/cloud-account/api';
import { NativeResources } from '@ariksa/inventory-core/api';
import { Box, HStack, Stack, useDisclosure } from '@chakra-ui/react';
import { findIndex } from 'lodash';
import startsWith from 'lodash/startsWith';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { getProviderAlias } from 'components/DataDisplay';
import { GrayIconButton } from 'components/DataEntry';
import { AIInsightIcon, TagIcon, ThirdPartyIcon } from 'components/Icons';
import { TabItemProps, Tabs } from 'components/Navigation';
import { MetadataDrawerSummary } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/Components/MetadataDrawerSummary';
import { CopyResourceLinkButton } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/CopyResourceLinkButton';
import { useMetadataDrawerTabs } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/hooks/useMetadataDrawerTabs';
import { usesDynamicSummary } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Summary/utils';
import { useActiveResourceContext } from 'containers/ActiveCloudResource/hooks/context/context';
import { useActiveResourceActions } from 'containers/ActiveCloudResource/hooks/useActiveResourceActions';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';
import { actions } from 'containers/ActiveCloudResource/slice';
import { useSearchParamFunctions } from 'containers/App/hooks/useSearchParamFunctions';
import { SummarizeResource } from 'containers/SharedState/Components/SummarizeResource';
import { useSearchParams } from 'hooks/useSearchParams';

export const ResourceMetadata: FC = () => {
  const { tabIndex } = useActiveResourceContext();
  const {
    resourceType,
    activeTabIndex,
    activeTabName,
    resourceInsight,
    resourceId,
    resourceUuid,
    resourceAccountId,
    cloudProvider,
  } = useSelector(selectActiveResource);
  const summarizeResource = useDisclosure();
  const dispatch = useDispatch();
  const {
    getResourceDetails,
    getResourceInsight,
    getVulnerabilitySummary,
    getEntitySeverity,
    getDynamicResourceDetails,
    getResourceRiskContext,
    getAMIHosts,
  } = useActiveResourceActions();
  const searchParams = useSearchParams<{ metadata_tab: string }>();
  const { updateValueInURL } = useSearchParamFunctions();
  const location = useLocation();

  useEffect(() => {
    getResourceInsight();
  }, [getResourceInsight]);

  // load resource data only when the drawer is opened
  useEffect(() => {
    getResourceDetails();

    if (usesDynamicSummary(resourceType, cloudProvider)) {
      getDynamicResourceDetails();
    }
  }, [
    getResourceDetails,
    getResourceInsight,
    getDynamicResourceDetails,
    resourceType,
    cloudProvider,
  ]);

  useEffect(() => {
    if (cloudProvider !== CloudProviders.Snowflake) {
      getResourceRiskContext();
      getEntitySeverity();
    } else if (resourceType === NativeResources.SnowflakeTables) {
      getResourceRiskContext();
    }
  }, [cloudProvider, getEntitySeverity, getResourceRiskContext, resourceType]);

  useEffect(() => {
    getVulnerabilitySummary();
  }, [getVulnerabilitySummary]);

  useEffect(() => {
    if (resourceType === NativeResources.AmazonMachineImage) getAMIHosts();
  }, [getAMIHosts, resourceType]);

  const { getMetadataDrawerTabs } = useMetadataDrawerTabs();
  const tabs: TabItemProps[] = getMetadataDrawerTabs(resourceType);

  useEffect(() => {
    // if tab name is present set the index accordingly
    //we are setting the tab from searchParams.metadata_tab in ResourceMetadataPage component
    if (!!searchParams.metadata_tab) return;
    if (!activeTabName) {
      dispatch(actions.setActiveTabName(tabs[activeTabIndex]?.key ?? ''));
    } else {
      const index = findIndex(tabs, t => t?.key === activeTabName);
      if (index === -1) {
        dispatch(actions.setActiveTabIndex(0));
        dispatch(actions.setActiveTabName(tabs[0]?.key));
      } else {
        dispatch(actions.setActiveTabIndex(index));
      }
    }
  }, [
    activeTabIndex,
    activeTabName,
    dispatch,
    tabs,
    searchParams.metadata_tab,
  ]);

  return (
    <Stack w="full" h="full" spacing={4}>
      <HStack spacing={3}>
        <GrayIconButton
          onClick={() => summarizeResource.onOpen()}
          leftIcon={<AIInsightIcon />}
          isDisabled
        >
          Summarize
        </GrayIconButton>
        <GrayIconButton
          onClick={() => summarizeResource.onOpen()}
          leftIcon={<ThirdPartyIcon strokeWidth={1.5} />}
          isDisabled
        >
          {getProviderAlias(cloudProvider ?? resourceInsight.data?.provider)}
        </GrayIconButton>
        <GrayIconButton
          onClick={() => summarizeResource.onOpen()}
          leftIcon={<TagIcon />}
          isDisabled
        >
          Add Context
        </GrayIconButton>
        <CopyResourceLinkButton
          label="Resource Link"
          tooltip={'Get resource link'}
        />
        <CopyResourceLinkButton
          label={activeTabName + ' Link'}
          tooltip={'Get ' + activeTabName + ' link'}
        />
      </HStack>
      <MetadataDrawerSummary />
      <Box h="full">
        <Tabs
          items={tabs}
          currentIndex={tabIndex}
          onChange={index => {
            dispatch(actions.setActiveTabIndex(index));
            dispatch(actions.setActiveTabName(tabs[index]?.key));
            if (
              !!searchParams.metadata_tab &&
              startsWith(location.pathname, '/inventory/cloud/resource')
            )
              updateValueInURL('metadata_tab', tabs[index]?.key);
          }}
          styles={{ tabPanel: { pb: 4 } }}
        />
      </Box>
      {summarizeResource.isOpen && (
        <SummarizeResource
          resource={{
            ...resourceInsight.data,
            resource_id: resourceId,
            account_id: resourceAccountId,
            uuid: resourceUuid,
            native_name: resourceType,
          }}
          useDisclosure={summarizeResource}
        />
      )}
    </Stack>
  );
};
